import React, { useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
} from "@mui/material";
import { motion } from "framer-motion";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

// Icons
import BusinessIcon from "@mui/icons-material/Business";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import PublicIcon from "@mui/icons-material/Public";
import PeopleIcon from "@mui/icons-material/People";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import VerifiedIcon from "@mui/icons-material/Verified";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import HandshakeIcon from "@mui/icons-material/Handshake";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TargetIcon from "@mui/icons-material/Gavel";
import FlagIcon from "@mui/icons-material/Flag";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import InventoryIcon from "@mui/icons-material/Inventory";
import GroupsIcon from "@mui/icons-material/Groups";

const AboutPage = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  // Title ayarı için useEffect ekleyelim
  useEffect(() => {
    document.title = `Safe Global Trade | ${
      i18n.language === "tr" ? "Kurumsal" : "About Us"
    }`;
  }, [i18n.language]);

  // Values data
  const valuesData = [
    {
      icon: <VerifiedIcon />,
      title: t("about.values.quality.title"),
      description: t("about.values.quality.desc"),
      color: theme.palette.primary.main,
    },
    {
      icon: <HandshakeIcon />,
      title: t("about.values.integrity.title"),
      description: t("about.values.integrity.desc"),
      color: theme.palette.secondary.main,
    },
    {
      icon: <PeopleIcon />,
      title: t("about.values.customer.title"),
      description: t("about.values.customer.desc"),
      color: "#2E7D32", // green
    },
    {
      icon: <TrendingUpIcon />,
      title: t("about.values.innovation.title"),
      description: t("about.values.innovation.desc"),
      color: "#D32F2F", // red
    },
  ];

  // Stats data for homepage style
  const statsData = [
    {
      icon: <WorkHistoryIcon fontSize="large" />,
      value: "337",
      label: i18n.language === "tr" ? "TEKLİF" : "QUOTES",
      color: theme.palette.primary.main,
    },
    {
      icon: <InventoryIcon fontSize="large" />,
      value: "267",
      label: i18n.language === "tr" ? "SİPARİŞ" : "ORDERS",
      color: theme.palette.secondary.main,
    },
    {
      icon: <LocalShippingIcon fontSize="large" />,
      value: "264",
      label: i18n.language === "tr" ? "SEVKİYAT" : "SHIPMENTS",
      color: "#2E7D32", // green
    },
    {
      icon: <GroupsIcon fontSize="large" />,
      value: "255",
      label: i18n.language === "tr" ? "MUTLU MÜŞTERİ" : "HAPPY CLIENTS",
      color: "#D32F2F", // red
    },
  ];

  // Mission goals - Dil desteği
  const missionGoals =
    i18n.language === "tr"
      ? [
          "Müşterilerimize istenilen ürünü, en uygun fiyat ve şartlarla temin etmek",
          "Tarım ürünleri sektöründe büyüyerek pazarda söz sahibi olmak",
          "Uzun yıllara dayanan tecrübemiz ve güvenilirliğimizle lider firmalardan olmak",
          "Fiyat avantajını doğrudan müşterilerimize yansıtmak",
        ]
      : [
          "To provide our customers with the desired products at the most affordable price and conditions",
          "To grow in the agricultural products sector and have a say in the market",
          "To be one of the leading companies with our long years of experience and reliability",
          "To reflect the price advantage directly to our customers",
        ];

  // Quick facts simplified
  const quickFacts = [
    {
      icon: <PublicIcon fontSize="large" />,
      title: t("about.quickFacts.founded"),
      description: "1999",
      color: theme.palette.primary.main,
    },
    {
      icon: <StarIcon fontSize="large" />,
      title: t("about.quickFacts.specialization"),
      description: t("about.quickFacts.specializationDesc"),
      color: theme.palette.secondary.main,
    },
    {
      icon: <LocalShippingIcon fontSize="large" />,
      title: t("about.quickFacts.operations"),
      description: t("about.quickFacts.operationsDesc"),
      color: "#2E7D32", // green
    },
  ];

  return (
    <Box sx={{ pb: 8 }}>
      {/* Hero Section - No margin between banner and header */}
      <Box
        sx={{
          position: "relative",
          height: { xs: 400, md: 500, lg: 600 },
          overflow: "hidden",
          mb: 8,
          // Add negative margin to start from top of page (below header)
          mt: "-70px", // Header height as negative margin (same as in HomeSlider)
          // Add padding top to compensate for the header height
          pt: { xs: "70px", md: "70px" },
          zIndex: 0, // Lower zIndex so header appears above
        }}
      >
        <Box
          component="img"
          src="/images/handshake1280.jpg"
          alt="Safe Global Trade Corporate"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            filter: "brightness(0.8)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.3))",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            px: 3,
            // Add padding top to push content down (adjust as needed for header)
            pt: { xs: "70px", md: "70px" },
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              color: "white",
              fontWeight: 700,
              textAlign: "center",
              fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4.5rem" },
              textShadow: "0 2px 8px rgba(0,0,0,0.5)",
              mb: 3,
              letterSpacing: 1,
            }}
          >
            {t("about.title")}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: "white",
              textAlign: "center",
              maxWidth: 900,
              fontWeight: 400,
              fontSize: { xs: "1.2rem", sm: "1.4rem", md: "1.7rem" },
              textShadow: "0 2px 8px rgba(0,0,0,0.5)",
              lineHeight: 1.6,
            }}
          >
            {t("about.subtitle")}
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="lg">
        {/* Biz Kimiz Section */}
        <Grid container spacing={6} sx={{ mb: 10 }}>
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, y: -30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  position: "relative",
                  display: "inline-block",
                  "&:after": {
                    content: '""',
                    position: "absolute",
                    bottom: -10,
                    left: 0,
                    width: 80,
                    height: 4,
                    backgroundColor: "primary.main",
                    borderRadius: 4,
                  },
                }}
              >
                {i18n.language === "tr" ? "Biz Kimiz" : "Who We Are"}
              </Typography>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="body1"
                sx={{ mb: 3, fontSize: "1.1rem", lineHeight: 1.8 }}
              >
                {i18n.language === "tr"
                  ? "1999 yılında temelleri atılan Safe Global Trade, tarım ürünleri ticaretinde güven ve kalite anlayışıyla sektörün öncü firmalarından biri olarak yolculuğuna başlamıştır. Kurulduğumuz günden bu yana küresel pazarda edindiğimiz deneyim ve oluşturduğumuz güçlü tedarik zinciriyle, müşterilerimize premium kalitede ürünler sunmakta ve her geçen gün hizmet ağımızı genişletmekteyiz."
                  : "Founded in 1999, Safe Global Trade began its journey as one of the leading companies in the agricultural products trade with an understanding of trust and quality. With the experience we have gained in the global market and the strong supply chain we have created since our establishment, we offer premium quality products to our customers and expand our service network every day."}
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}
              >
                {i18n.language === "tr"
                  ? "SGT olarak sürdürülebilirlik, kalite ve müşteri memnuniyeti odaklı yaklaşımımızla, değişen pazar koşullarına hızla adapte olarak büyümeye devam ediyoruz. Uluslararası standartlarda çalışma prensibimiz ve güvenilir iş ilişkilerimiz ile global ticaret dünyasında saygın bir konum elde etmiş bulunmaktayız."
                  : "As SGT, we continue to grow by rapidly adapting to changing market conditions with our approach focused on sustainability, quality and customer satisfaction. We have achieved a respected position in the global trade world with our international standards of working principles and reliable business relationships."}
              </Typography>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    backgroundColor: "rgba(250, 250, 250, 0.8)",
                    boxShadow: "0 10px 40px rgba(0,0,0,0.07)",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "1.1rem",
                      lineHeight: 1.8,
                      fontStyle: "italic",
                      color: "text.secondary",
                    }}
                  >
                    {i18n.language === "tr"
                      ? "Safe Global Trade, uluslararası tarım ticaretinde güvenilir bir ortak olarak, müşteri memnuniyetini her zaman ön planda tutarak faaliyetlerini sürdürmektedir. Sektördeki 20+ yıllık deneyimimizle, kalite ve sürdürülebilirliği temel alan yaklaşımımızla değer yaratmaya devam ediyoruz."
                      : "Safe Global Trade continues its activities as a reliable partner in international agricultural trade, always prioritizing customer satisfaction. With our 20+ years of experience in the industry, we continue to create value with our approach based on quality and sustainability."}
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>

        {/* Hızlı Bilgiler ve İstatistikler - Tam Genişlikte */}
        <Box
          sx={{
            width: "100%",
            mb: 10,
            bgcolor: alpha(theme.palette.primary.main, 0.03),
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Box
              sx={{
                bgcolor: "primary.main",
                py: 2,
                px: 4,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <BusinessIcon sx={{ color: "white", fontSize: 28 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: "white" }}>
                {t("about.quickFacts.title")}
              </Typography>
            </Box>

            <Grid container>
              {/* Hızlı Bilgiler - Modern ve Renkli (SOLDA) */}
              <Grid
                item
                xs={12}
                md={5}
                sx={{
                  p: 4,
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                }}
              >
                {quickFacts.map((fact, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: alpha(fact.color, 0.15),
                        color: fact.color,
                        width: 60,
                        height: 60,
                        flexShrink: 0,
                        boxShadow: "0 6px 15px rgba(0,0,0,0.08)",
                      }}
                    >
                      {fact.icon}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        {fact.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {fact.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Grid>

              {/* İstatistikler - Modern Tasarım (SAĞDA) */}
              <Grid
                item
                xs={12}
                md={7}
                sx={{
                  bgcolor: "white",
                  p: 4,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Grid
                  container
                  spacing={3}
                  sx={{
                    width: "100%",
                    ml: 0,
                    pr: { xs: 0, md: 0 },
                    "& .MuiGrid-item": {
                      pl: { xs: 1.5, md: 1.5 },
                      pr: { xs: 1.5, md: 1.5 },
                    },
                  }}
                >
                  {statsData.map((stat, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={3}
                      key={index}
                      sx={{
                        mb: 2,
                        pr: { xs: 1, md: index === 3 ? 0 : 1 },
                      }}
                    >
                      <Box
                        sx={{
                          p: { xs: 2, md: 3 },
                          borderRadius: 3,
                          bgcolor: alpha(stat.color, 0.05),
                          border: `1px solid ${alpha(stat.color, 0.1)}`,
                          height: "100%",
                          transition: "all 0.25s ease",
                          "&:hover": {
                            transform: "translateY(-5px)",
                            boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                            bgcolor: stat.color,
                            "& .stat-icon, & .stat-value, & .stat-label": {
                              color: "white",
                            },
                          },
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                        }}
                      >
                        <Box
                          className="stat-icon"
                          sx={{
                            color: stat.color,
                            mb: 1,
                          }}
                        >
                          {stat.icon}
                        </Box>
                        <Typography
                          className="stat-value"
                          variant="h5"
                          component="p"
                          sx={{
                            fontWeight: 700,
                            color: stat.color,
                            mb: 0.5,
                            fontSize: { xs: "1.8rem", md: "2rem" },
                          }}
                        >
                          {stat.value}
                        </Typography>
                        <Typography
                          className="stat-label"
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: "text.secondary",
                            letterSpacing: 0.5,
                            fontSize: { xs: "0.75rem", md: "0.9rem" },
                          }}
                        >
                          {stat.label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </motion.div>
        </Box>

        {/* Mission & Vision - Yan Yana */}
        <Grid container spacing={4} sx={{ mb: 10 }}>
          {/* Mission Section */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              style={{ height: "100%" }}
            >
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  background: "linear-gradient(145deg, #EBF3FE, #F5F9FF)",
                  borderRadius: 4,
                  overflow: "visible",
                  position: "relative",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  boxShadow: "0 15px 35px rgba(0,0,0,0.05)",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: -25,
                    left: 30,
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "50%",
                    width: 70,
                    height: 70,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                  }}
                >
                  <TrackChangesIcon sx={{ color: "white", fontSize: 35 }} />
                </Box>
                <CardContent sx={{ pt: 6, px: 4, pb: 4 }}>
                  <Typography
                    variant="h4"
                    component="h3"
                    sx={{ fontWeight: 700, mb: 3 }}
                  >
                    {t("about.mission.title")}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontSize: "1.05rem", lineHeight: 1.8, mb: 3 }}
                  >
                    {t("about.mission.description")}
                  </Typography>

                  <Box
                    sx={{
                      mt: 3,
                      p: 3,
                      borderRadius: 3,
                      bgcolor: "white",
                      boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        color: theme.palette.primary.main,
                      }}
                    >
                      <TargetIcon sx={{ mr: 1, color: "primary.main" }} />
                      {i18n.language === "tr" ? "Hedeflerimiz" : "Our Goals"}
                    </Typography>

                    <List disablePadding sx={{ pl: 1 }}>
                      {missionGoals.map((goal, index) => (
                        <ListItem
                          key={index}
                          disableGutters
                          sx={{ py: 0.75, px: 0 }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircleOutlineIcon
                              color="primary"
                              fontSize="small"
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={goal}
                            sx={{
                              "& .MuiTypography-root": {
                                fontSize: "0.95rem",
                                fontWeight: 500,
                              },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Vision Section */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              style={{ height: "100%" }}
            >
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  background: "linear-gradient(145deg, #FFF0E6, #FFF5EB)",
                  borderRadius: 4,
                  overflow: "visible",
                  position: "relative",
                  border: `1px solid ${alpha(
                    theme.palette.secondary.main,
                    0.2
                  )}`,
                  boxShadow: "0 15px 35px rgba(0,0,0,0.05)",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: -25,
                    left: 30,
                    backgroundColor: theme.palette.secondary.main,
                    borderRadius: "50%",
                    width: 70,
                    height: 70,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                  }}
                >
                  <VisibilityIcon sx={{ color: "white", fontSize: 35 }} />
                </Box>
                <CardContent sx={{ pt: 6, px: 4, pb: 4 }}>
                  <Typography
                    variant="h4"
                    component="h3"
                    sx={{ fontWeight: 700, mb: 3 }}
                  >
                    {t("about.vision.title")}
                  </Typography>

                  <Box
                    sx={{
                      backgroundColor: "white",
                      p: 3,
                      borderRadius: 3,
                      border: `1px solid ${alpha(
                        theme.palette.secondary.main,
                        0.2
                      )}`,
                      mb: 3,
                      boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: "1.05rem",
                        fontWeight: 500,
                        fontStyle: "italic",
                        lineHeight: 1.7,
                        color: theme.palette.secondary.dark,
                      }}
                    >
                      {i18n.language === "tr"
                        ? '"Güvenilirlik, süreklilik, insan ilişkisine ve sadakate önem veren bir marka olmak."'
                        : '"To be a brand that values reliability, continuity, human relationships and loyalty."'}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{ fontSize: "1.05rem", lineHeight: 1.8, mb: 3 }}
                  >
                    {t("about.vision.description")}
                  </Typography>

                  <Box
                    sx={{
                      mt: 3,
                      p: 3,
                      borderRadius: 3,
                      bgcolor: "white",
                      display: "flex",
                      alignItems: "center",
                      boxShadow: "0 5px 15px rgba(0,0,0,0.05)",
                    }}
                  >
                    <FlagIcon
                      sx={{
                        color: theme.palette.secondary.main,
                        mr: 2,
                        fontSize: 30,
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        fontSize: "1.1rem",
                        color: theme.palette.secondary.main,
                      }}
                    >
                      {i18n.language === "tr"
                        ? "Kalite ve güven odaklı yaklaşım"
                        : "Quality and trust-focused approach"}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        {/* Company Values - Eşit Boyutlu Tek Satırda */}
        <Box sx={{ mb: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                textAlign: "center",
                position: "relative",
                display: "inline-block",
                left: "50%",
                transform: "translateX(-50%)",
                "&:after": {
                  content: '""',
                  position: "absolute",
                  bottom: -10,
                  left: "25%",
                  width: "50%",
                  height: 4,
                  backgroundColor: "primary.main",
                  borderRadius: 4,
                },
              }}
            >
              {t("about.values.title")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 6,
                mt: 3,
                textAlign: "center",
                fontSize: "1.1rem",
                maxWidth: 800,
                mx: "auto",
              }}
            >
              {t("about.values.subtitle")}
            </Typography>
          </motion.div>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gap: 3,
              "& > div": {
                height: "100%",
              },
            }}
          >
            {valuesData.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                style={{ height: "100%" }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 4,
                    transition: "all 0.3s ease",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    "&:hover": {
                      transform: "translateY(-10px)",
                      boxShadow: "0 15px 35px rgba(0,0,0,0.12)",
                      "& .value-header": {
                        bgcolor: value.color,
                        "& .value-avatar": {
                          bgcolor: "white",
                          color: value.color,
                        },
                        "& .value-title": {
                          color: "white",
                        },
                      },
                    },
                  }}
                >
                  <Box
                    className="value-header"
                    sx={{
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      bgcolor: alpha(value.color, 0.1),
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Avatar
                      className="value-avatar"
                      sx={{
                        bgcolor: "white",
                        color: value.color,
                        width: 60,
                        height: 60,
                        mb: 2,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {value.icon}
                    </Avatar>
                    <Typography
                      className="value-title"
                      variant="h6"
                      component="h3"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {value.title}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 3, flexGrow: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.7,
                        textAlign: "center",
                      }}
                    >
                      {value.description}
                    </Typography>
                  </Box>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default AboutPage;